import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, User, CreditCard, Settings, Award, Loader2, Save, Moon, Sun, BookOpen, MessageSquare, Crown, Star, Infinity } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import BadgeDisplay from '@/components/BadgeDisplay';

const API = `${window.location.origin}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [stats, setStats] = useState({ courses: 0, completed: 0, debates: 0 });
  const [profileData, setProfileData] = useState({ name: '', email: '' });
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [settings, setSettings] = useState({ email_notifications: true, display_preference: 'default' });
  const [cancelling, setCancelling] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    // Try to load from cache first for instant UI
    const cacheKey = 'profile_data_cache';
    const cached = sessionStorage.getItem(cacheKey);
    const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);
    const CACHE_TTL = 60000; // 1 minute cache
    
    if (cached && cacheTimestamp) {
      const age = Date.now() - parseInt(cacheTimestamp);
      if (age < CACHE_TTL) {
        try {
          const cachedData = JSON.parse(cached);
          setSubscription(cachedData.subscription);
          setUsageStats(cachedData.usageStats);
          setStats(cachedData.stats);
          setSettings(cachedData.settings);
          setProfileData({
            name: user?.name || '',
            email: user?.email || ''
          });
          setLoading(false);
          // Still refresh in background
          refreshProfileDataInBackground();
          return;
        } catch (e) {
          console.error('Cache parse error:', e);
        }
      }
    }
    
    // No cache or expired, fetch fresh
    await fetchProfileData();
  };
  
  const refreshProfileDataInBackground = async () => {
    try {
      await fetchProfileData(true);
    } catch (error) {
      console.error('Background refresh failed:', error);
    }
  };
  
  const fetchProfileData = async (isBackground = false) => {
    try {
      const [subRes, progressRes, coursesRes, debatesRes, settingsRes, usageRes] = await Promise.all([
        axios.get(`${API}/subscription/check`, { withCredentials: true }),
        axios.get(`${API}/progress`, { withCredentials: true }),
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/debate/list/all`, { withCredentials: true }),
        axios.get(`${API}/user/settings`, { withCredentials: true }),
        axios.get(`${API}/subscription/usage`, { withCredentials: true })
      ]);

      const subscriptionData = subRes.data;
      const completed = progressRes.data.filter(p => p.completion_percentage >= 100).length;
      const statsData = {
        courses: coursesRes.data.length,
        completed,
        debates: debatesRes.data.length
      };
      const settingsData = {
        email_notifications: settingsRes.data.email_notifications,
        display_preference: settingsRes.data.display_preference
      };
      
      setSubscription(subscriptionData);
      setUsageStats(usageRes.data);
      setStats(statsData);
      setSettings(settingsData);
      setProfileData({
        name: user?.name || '',
        email: user?.email || ''
      });
      
      // Cache the data
      const cacheKey = 'profile_data_cache';
      sessionStorage.setItem(cacheKey, JSON.stringify({
        subscription: subscriptionData,
        usageStats: usageRes.data,
        stats: statsData,
        settings: settingsData
      }));
      sessionStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());
      
    } catch (error) {
      if (!isBackground) {
        toast.error('Failed to load profile data');
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/auth/update-profile`,
        { name: profileData.name },
        { withCredentials: true }
      );
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/auth/change-password`,
        { current_password: passwordData.current, new_password: passwordData.new },
        { withCredentials: true }
      );
      toast.success('Password changed successfully');
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSettings = async (key, value) => {
    try {
      await axios.put(
        `${API}/user/settings`,
        { [key]: value },
        { withCredentials: true }
      );
      setSettings({ ...settings, [key]: value });
      // Invalidate cache since settings changed
      sessionStorage.removeItem('profile_data_cache');
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) {
      return;
    }

    setCancelling(true);
    try {
      await axios.delete(`${API}/subscription/cancel`, { withCredentials: true });
      toast.success('Subscription cancelled successfully');
      // Invalidate cache and reload subscription data
      sessionStorage.removeItem('profile_data_cache');
      const subRes = await axios.get(`${API}/subscription/check`, { withCredentials: true });
      setSubscription(subRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteAccount = async () => {
    // Check if user has active subscription
    if (subscription?.has_subscription && !subscription?.is_trial) {
      const confirmCancel = window.confirm(
        'You have an active paid subscription. It will be cancelled before deleting your account. Continue?'
      );
      if (!confirmCancel) return;
    }

    const finalConfirm = window.confirm(
      'Are you absolutely sure you want to delete your account? This action CANNOT be undone. All your courses, progress, and data will be permanently deleted.'
    );
    
    if (!finalConfirm) return;

    const typeConfirm = window.prompt(
      'Type "DELETE" in capital letters to confirm account deletion:'
    );

    if (typeConfirm !== 'DELETE') {
      toast.error('Account deletion cancelled - confirmation text did not match');
      return;
    }

    setSaving(true);
    try {
      await axios.delete(`${API}/user/account`, { withCredentials: true });
      toast.success('Account deleted successfully. Redirecting...');
      setTimeout(() => {
        logout();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await axios.put(
            `${API}/auth/update-photo`,
            { photo: reader.result },
            { withCredentials: true }
          );
          toast.success('Profile photo updated successfully');
          // Reload page to show new photo
          window.location.reload();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to update photo');
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center theme-bg">
        <Loader2 className="w-12 h-12 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg">
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-black/90 border-amber-900/50' : 'bg-white/80 border-amber-200'} backdrop-blur-sm border-b shadow-sm`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-2">
              <User className={`w-6 h-6 ${isDarkMode ? 'text-amber-500' : 'text-amber-700'}`} />
              <span className={`text-xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Profile & Settings</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Profile Header */}
        <Card className={`card-glow mb-8 ${isDarkMode ? 'bg-gray-900 border-amber-700' : 'border-amber-200'}`}>
          <CardContent className="p-6">
            <div className="flex items-center space-x-6">
              <div className="relative">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className={`w-20 h-20 rounded-full border-4 ${isDarkMode ? 'border-amber-500' : 'border-amber-200'} object-cover`} />
                ) : (
                  <div className={`w-20 h-20 rounded-full ${isDarkMode ? 'bg-amber-700' : 'bg-amber-200'} flex items-center justify-center`}>
                    <User className={`w-10 h-10 ${isDarkMode ? 'text-amber-200' : 'text-amber-700'}`} />
                  </div>
                )}
                <label 
                  htmlFor="photo-upload" 
                  className="absolute bottom-0 right-0 bg-amber-600 hover:bg-amber-700 text-white rounded-full p-2 cursor-pointer shadow-lg transition-colors"
                  title="Upload photo"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
                    </svg>
                  )}
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                  className="hidden"
                  data-testid="photo-upload-input"
                />
              </div>
              <div className="flex-1">
                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>{user?.name}</h2>
                <p className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>{user?.email}</p>
                {subscription?.is_trial && subscription?.days_remaining > 0 && (
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    Free Trial - {subscription.days_remaining} days remaining
                  </p>
                )}
                {subscription?.has_subscription && !subscription?.is_trial && (
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                    ✓ Active Subscription
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-6" data-testid="stats-overview">
          <Card className={`card-glow backdrop-blur-sm ${isDarkMode ? 'bg-gray-900 border-amber-700' : 'border-amber-200 bg-white/90'}`}>
            <CardContent className="flex items-center gap-3 p-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-amber-900/40' : 'bg-amber-100'}`}>
                <BookOpen className={`w-5 h-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
              </div>
              <div>
                <p className={`text-2xl font-bold leading-none ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>{stats.courses}</p>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Courses Enrolled</p>
              </div>
            </CardContent>
          </Card>

          <Card className={`card-glow backdrop-blur-sm ${isDarkMode ? 'bg-gray-900 border-green-700' : 'border-green-200 bg-white/90'}`}>
            <CardContent className="flex items-center gap-3 p-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-green-900/40' : 'bg-green-100'}`}>
                <Award className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <div>
                <p className={`text-2xl font-bold leading-none ${isDarkMode ? 'text-green-100' : 'text-green-900'}`}>{stats.completed}</p>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>Completed</p>
              </div>
            </CardContent>
          </Card>

          <Card className={`card-glow backdrop-blur-sm ${isDarkMode ? 'bg-gray-900 border-purple-700' : 'border-purple-200 bg-white/90'}`}>
            <CardContent className="flex items-center gap-3 p-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-purple-900/40' : 'bg-purple-100'}`}>
                <MessageSquare className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <div>
                <p className={`text-2xl font-bold leading-none ${isDarkMode ? 'text-purple-100' : 'text-purple-900'}`}>{stats.debates}</p>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>Debate Sessions</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Badge Display */}
        <div className="mb-8" data-testid="badge-section">
          <BadgeDisplay />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="account" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="account" data-testid="account-tab">
              <User className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
            <TabsTrigger value="subscription" data-testid="subscription-tab">
              <CreditCard className="w-4 h-4 mr-2" />
              Subscription
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Account Tab */}
          <TabsContent value="account">
            <Card className="card-glow border-amber-200">
              <CardHeader>
                <CardTitle className={`${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Account Information</CardTitle>
                <CardDescription className={`${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  Update your profile information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={profileData.email}
                    disabled
                    className={`${isDarkMode ? 'bg-gray-700 text-amber-100 border-gray-600' : 'bg-gray-100 text-gray-900'}`}
                    data-testid="profile-email-input"
                  />
                  <p className={`text-xs ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Email cannot be changed</p>
                </div>
                <Button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="save-profile-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>

                {user?.auth_type === 'jwt' && (
                  <>
                    <hr className="my-6" />
                    <div className="space-y-4">
                      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Change Password</h3>
                      <div className="space-y-2">
                        <Label htmlFor="current-password">Current Password</Label>
                        <Input
                          id="current-password"
                          type="password"
                          value={passwordData.current}
                          onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                          data-testid="current-password-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-password">New Password</Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={passwordData.new}
                          onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                          data-testid="new-password-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">Confirm New Password</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={passwordData.confirm}
                          onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                          data-testid="confirm-password-input"
                        />
                      </div>
                      <Button
                        onClick={handleChangePassword}
                        disabled={saving}
                        variant="outline"
                        data-testid="change-password-btn"
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Change Password
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <Card className="card-glow border-amber-200">
              <CardHeader>
                <CardTitle className={`${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Subscription Details</CardTitle>
                <CardDescription className={`${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  Manage your subscription and view usage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Plan Status */}
                {subscription?.is_cancelled ? (
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-orange-700' : 'bg-orange-50 border-orange-200'}`}>
                    <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-orange-200' : 'text-orange-900'}`}>
                      {subscription?.is_trial ? 'Trial Cancelled' : 'Subscription Cancelled'}
                    </h3>
                    <p className={`mb-2 ${isDarkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                      You still have access for {subscription.days_remaining} days
                    </p>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}>
                      {subscription?.is_trial 
                        ? 'Your trial has been cancelled and will not convert to a paid subscription.'
                        : 'Your subscription has been cancelled and will not renew.'}
                    </p>
                    <Button
                      onClick={() => navigate('/subscription')}
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid="reactivate-subscription-btn"
                    >
                      Reactivate Subscription
                    </Button>
                  </div>
                ) : subscription?.is_trial ? (
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Star className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      <h3 className={`text-lg font-bold ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>Free Trial</h3>
                    </div>
                    <p className={`mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                      {subscription.days_remaining} days remaining in your trial
                    </p>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      Upgrade to unlock more courses and debates!
                    </p>
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => navigate('/subscription')}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="upgrade-subscription-btn"
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Upgrade Now
                      </Button>
                      <Button
                        onClick={handleCancelSubscription}
                        disabled={cancelling}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid="cancel-trial-btn"
                      >
                        {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Cancel Trial
                      </Button>
                    </div>
                  </div>
                ) : subscription?.has_subscription ? (
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-green-700' : 'bg-green-50 border-green-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {usageStats?.plan_type === 'platinum' ? (
                        <Infinity className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                      ) : usageStats?.plan_type === 'pro' ? (
                        <Crown className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                      ) : (
                        <Star className={`w-5 h-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      )}
                      <h3 className={`text-lg font-bold ${isDarkMode ? 'text-green-200' : 'text-green-900'}`}>
                        {usageStats?.plan_type?.charAt(0).toUpperCase() + usageStats?.plan_type?.slice(1) || 'Active'} Plan
                      </h3>
                    </div>
                    <p className={`mb-2 ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>
                      ${usageStats?.price || '9.99'}/month
                    </p>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                      You have full access to all features
                    </p>
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => navigate('/subscription')}
                        variant="outline"
                        className={`${isDarkMode ? 'border-green-600 text-green-400 hover:bg-green-900/30' : 'border-green-600 text-green-700 hover:bg-green-50'}`}
                        data-testid="change-plan-btn"
                      >
                        Change Plan
                      </Button>
                      <Button
                        onClick={handleCancelSubscription}
                        disabled={cancelling}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid="cancel-subscription-btn"
                      >
                        {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-red-700' : 'bg-red-50 border-red-200'}`}>
                    <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-red-200' : 'text-red-900'}`}>No Active Subscription</h3>
                    <p className={`mb-4 ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                      Subscribe to access all features
                    </p>
                    <Button
                      onClick={() => navigate('/subscription')}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="subscribe-now-btn"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Subscribe Now
                    </Button>
                  </div>
                )}

                {/* Usage Statistics */}
                {usageStats && usageStats.plan_type !== 'none' && (
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                    <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>
                      Monthly Usage
                    </h4>
                    
                    {/* Courses Usage */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <BookOpen className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>Courses</span>
                        </div>
                        <span className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                          {usageStats.courses_limit === -1 ? (
                            <>{usageStats.courses_used} used (Unlimited)</>
                          ) : (
                            <>
                              {usageStats.courses_used} / {usageStats.courses_limit + usageStats.courses_rollover}
                              {usageStats.courses_rollover > 0 && (
                                <span className="text-green-500 ml-1">(+{usageStats.courses_rollover} rollover)</span>
                              )}
                            </>
                          )}
                        </span>
                      </div>
                      {usageStats.courses_limit !== -1 && (
                        <Progress 
                          value={Math.min((usageStats.courses_used / (usageStats.courses_limit + usageStats.courses_rollover)) * 100, 100)} 
                          className="h-2"
                        />
                      )}
                    </div>

                    {/* Debates Usage */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <MessageSquare className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>Debates</span>
                        </div>
                        <span className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                          {usageStats.debates_limit === -1 ? (
                            <>{usageStats.debates_used} used (Unlimited)</>
                          ) : (
                            <>
                              {usageStats.debates_used} / {usageStats.debates_limit + usageStats.debates_rollover}
                              {usageStats.debates_rollover > 0 && (
                                <span className="text-green-500 ml-1">(+{usageStats.debates_rollover} rollover)</span>
                              )}
                            </>
                          )}
                        </span>
                      </div>
                      {usageStats.debates_limit !== -1 && (
                        <Progress 
                          value={Math.min((usageStats.debates_used / (usageStats.debates_limit + usageStats.debates_rollover)) * 100, 100)} 
                          className="h-2"
                        />
                      )}
                    </div>

                    {/* Daily Course Cap for Platinum */}
                    {usageStats.daily_course_cap && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>Daily Courses</span>
                          <span className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                            {usageStats.daily_courses_used} / {usageStats.daily_course_cap} today
                          </span>
                        </div>
                        <Progress 
                          value={(usageStats.daily_courses_used / usageStats.daily_course_cap) * 100} 
                          className="h-2"
                        />
                      </div>
                    )}

                    {/* Rollover Info */}
                    {(usageStats.plan_type === 'base' || usageStats.plan_type === 'pro') && (
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-3`}>
                        Unused credits roll over to next month (up to your monthly limit)
                      </p>
                    )}
                  </div>
                )}

                {/* Plan Features */}
                <div className={`pt-4 border-t ${isDarkMode ? 'border-amber-700' : 'border-amber-200'}`}>
                  <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Plan Features</h4>
                  <ul className={`space-y-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-500">✓</span>
                      <span>Unlimited AI chat with Professor Clio</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-500">✓</span>
                      <span>Access to all complexity levels</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-500">✓</span>
                      <span>AI-generated quizzes & feedback</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-500">✓</span>
                      <span>Progress tracking and analytics</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-500">✓</span>
                      <span>Debate room access</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="card-glow border-amber-200">
              <CardHeader>
                <CardTitle className={`${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Account Settings</CardTitle>
                <CardDescription className={`${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  Manage your account preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-amber-50'}`}>
                    <div>
                      <h4 className={`font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Account Type</h4>
                      <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                        {user?.auth_type === 'google' ? 'Google Account' : 'Email Account'}
                      </p>
                    </div>
                  </div>

                  <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-amber-50'}`}>
                    <div>
                      <h4 className={`font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Member Since</h4>
                      <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                        {new Date(user?.created_at || Date.now()).toLocaleDateString('en-US', {
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                <hr />

                <div className="space-y-4">
                  <h4 className={`font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'} mb-4`}>Preferences</h4>
                  
                  {/* Dark Mode Toggle */}
                  <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-amber-50'}`}>
                    <div className="flex items-center space-x-3">
                      {isDarkMode ? (
                        <Moon className={`w-5 h-5 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`} />
                      ) : (
                        <Sun className={`w-5 h-5 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`} />
                      )}
                      <div>
                        <h4 className={`font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Dark Mode</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                          {isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={isDarkMode}
                      onCheckedChange={toggleDarkMode}
                      data-testid="dark-mode-switch"
                    />
                  </div>
                  
                  <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-amber-50'}`}>
                    <div>
                      <h4 className={`font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Email Notifications</h4>
                      <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                        Receive updates about courses and features
                      </p>
                    </div>
                    <Switch
                      checked={settings.email_notifications}
                      onCheckedChange={(checked) => handleUpdateSettings('email_notifications', checked)}
                      data-testid="email-notifications-switch"
                    />
                  </div>
                </div>

                <hr />

                <div className="space-y-4">
                  <h4 className={`font-semibold ${isDarkMode ? 'text-amber-100' : 'text-red-900'}`}>Danger Zone</h4>
                  <div className={`p-4 rounded-lg space-y-4 ${isDarkMode ? 'bg-gray-800 border border-red-700' : 'bg-red-50 border-2 border-red-200'}`}>
                    <div>
                      <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-900'}`}>Delete Account</h5>
                      <p className={`text-sm mb-3 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                        Once you delete your account, there is no going back. All your courses, progress, and data will be permanently deleted.
                      </p>
                      {subscription?.has_subscription && !subscription?.is_trial && (
                        <p className={`text-sm mb-3 font-medium ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                          ⚠️ You have an active paid subscription. It will be automatically cancelled before deletion.
                        </p>
                      )}
                      <Button
                        onClick={handleDeleteAccount}
                        disabled={saving}
                        variant="destructive"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="delete-account-btn"
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Delete My Account
                      </Button>
                    </div>
                  </div>
                  <Button
                    onClick={logout}
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="logout-btn"
                  >
                    Log Out
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ProfilePage;
